import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';

export type LoadViewSuggestionTmsSupport = {
  carrierInfo: boolean;
  appointment: boolean;
};

// Keep in sync with LOAD_VIEW_TMS_SUPPORT.md
export const LOAD_VIEW_SUGGESTION_TMS_SUPPORT: Record<
  TMS,
  LoadViewSuggestionTmsSupport
> = {
  [TMS.McleodEnterprise]: { carrierInfo: true, appointment: true },
  [TMS.Mcleod]: { carrierInfo: true, appointment: true },
  [TMS.Aljex]: { carrierInfo: true, appointment: true },
  [TMS.ThreeG]: { carrierInfo: true, appointment: true },
  [TMS.Turvo]: { carrierInfo: true, appointment: true },
  [TMS.Stark]: { carrierInfo: true, appointment: true },
  [TMS.FreightFlow]: { carrierInfo: true, appointment: true },
  [TMS.Relay]: { carrierInfo: true, appointment: true },
  [TMS.Tai]: { carrierInfo: false, appointment: true },
  [TMS.Revenova]: { carrierInfo: false, appointment: true },
  [TMS.GlobalTranzTMS]: { carrierInfo: false, appointment: false },
  [TMS.MercurygateBoomi]: { carrierInfo: false, appointment: true },
  [TMS.TransportPro]: { carrierInfo: false, appointment: true },
  [TMS.QuantumEdge]: { carrierInfo: false, appointment: true },
  [TMS.PLS]: { carrierInfo: false, appointment: false },
};

const TMS_LABELS: Partial<Record<TMS, string>> = {
  [TMS.GlobalTranzTMS]: 'GlobalTranz',
  [TMS.MercurygateBoomi]: 'MercuryGate',
  [TMS.McleodEnterprise]: 'McLeod Enterprise',
  [TMS.ThreeG]: '3G',
  [TMS.TransportPro]: 'TransportPro',
  [TMS.QuantumEdge]: 'QuantumEdge',
};

function tmsLabel(tmsName: TMS): string {
  return TMS_LABELS[tmsName] ?? tmsName;
}

export function getLoadViewSuggestionTmsSupport(
  tmsName: Maybe<string>
): Maybe<LoadViewSuggestionTmsSupport> {
  if (!tmsName || !(tmsName in LOAD_VIEW_SUGGESTION_TMS_SUPPORT)) {
    return null;
  }
  return LOAD_VIEW_SUGGESTION_TMS_SUPPORT[tmsName as TMS];
}

// Returns a hover tooltip when the current TMS cannot apply this Load View suggestion.
export function getLoadViewSuggestionUnsupportedReason(
  suggestion: GenericSuggestion,
  tmsName: Maybe<string>
): Maybe<string> {
  const support = getLoadViewSuggestionTmsSupport(tmsName);
  if (!support || !tmsName) {
    return null;
  }

  const label = tmsLabel(tmsName as TMS);

  if (
    suggestion.pipeline === SuggestionPipelines.CarrierInfo &&
    !support.carrierInfo
  ) {
    return `${label} does not expose editable carrier fields in Load View, so Carrier Info suggestions cannot be applied.`;
  }

  if (
    suggestion.pipeline === SuggestionPipelines.ApptConfirmation &&
    !support.appointment
  ) {
    return `${label} does not expose appointment fields in Load View, so Appointment suggestions cannot be applied.`;
  }

  return null;
}

export function isLoadViewSuggestionUnsupportedForTms(
  suggestion: GenericSuggestion,
  tmsName: Maybe<string>
): boolean {
  return !!getLoadViewSuggestionUnsupportedReason(suggestion, tmsName);
}
